import { type ReactNode } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogFooter,
} from '@/components/ui/dialog';

import { Button, type ButtonVariants } from '../ui/button';

interface ConfirmationPopupProps {
  isOpen: boolean;
  title: string;
  children: ReactNode;
  confirmText?: string;
  cancelText?: string;
  hideCancel?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  confirmColor?: ButtonVariants['variant'];
  isLoading?: boolean;
  statusMessage?: string;
  statusType?: 'success' | 'error' | 'info' | 'warning';
  className?: string;
}

const ConfirmationPopup = ({
  isOpen,
  title,
  children,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  hideCancel = false,
  onConfirm,
  onCancel,
  confirmColor = 'destructive',
  isLoading = false,
  statusMessage,
  statusType = 'error',
  className,
}: ConfirmationPopupProps) => {
  return (
    <Dialog
      open={isOpen}
      // Not closable while the confirmed action runs (Escape, outside click, ✕):
      // closing would hide an action that is still going on.
      onOpenChange={(open) => {
        if (!open && !isLoading) {
          onCancel();
        }
      }}
    >
      <DialogContent className={className}>
        <DialogTitle>{title}</DialogTitle>
        {children}
        {statusMessage && (
          <div className={`status ${statusType}`}>{statusMessage}</div> //TODO: add a status message component
        )}
        <DialogFooter>
          {!hideCancel && (
            <Button
              type="button"
              onClick={onCancel}
              variant="ghost"
              disabled={isLoading}
            >
              {cancelText}
            </Button>
          )}
          <Button
            type="button"
            onClick={onConfirm}
            variant={confirmColor}
            disabled={isLoading || !!statusMessage}
          >
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmationPopup;
